import { useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { Upload, X, Loader2, Image as ImageIcon, GripVertical, RefreshCw, Crop, CheckCircle, AlertCircle, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ImageCropper } from './ImageCropper';
import { chunkedUpload, validateFile } from '@/utils/chunkedUpload';
import { getImageUrl as getBaseImageUrl } from '@/utils/imageUrl';

// Helper to get full image URL with cache-busting for uploads
const getImageUrl = (path) => {
  if (!path) return null;
  const baseUrl = getBaseImageUrl(path);
  if (!baseUrl) return null;
  // Add cache buster for freshly uploaded images
  const cacheBuster = `?v=${Date.now()}`;
  return baseUrl.includes('?') ? baseUrl : `${baseUrl}${cacheBuster}`;
};

export function ImageUploader({ 
  coverImage, 
  gallery: galleryProp = [], 
  onCoverChange = () => {}, 
  onGalleryChange = () => {},
  onUploadingChange = () => {},
  maxGalleryImages = 10  // Limit 10 zdjęć w galerii
}) {
  // Ensure gallery is always an array
  const gallery = Array.isArray(galleryProp) ? galleryProp : [];
  
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(''); // 'uploading', 'processing', 'error', 'success'
  const [statusMessage, setStatusMessage] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [lastFile, setLastFile] = useState(null);
  const coverInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const abortControllerRef = useRef(null);
  
  // Cropper state
  const [showCropper, setShowCropper] = useState(false);
  const [cropperImage, setCropperImage] = useState(null);
  const [cropperType, setCropperType] = useState('cover');
  const [pendingFile, setPendingFile] = useState(null);

  // Notify parent when uploading status changes
  const setUploadingWithNotify = useCallback((isUploading) => {
    setUploading(isUploading);
    onUploadingChange(isUploading);
  }, [onUploadingChange]);

  /**
   * Compress image client-side before upload.
   * Max 1920px longest side, JPEG quality 0.82.
   * Skips if file is already < 500KB.
   */
  const compressImage = (file) => {
    return new Promise((resolve) => {
      // Skip compression for small files
      if (file.size < 500 * 1024) {
        resolve(file);
        return;
      }

      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.onload = () => {
          const MAX_DIM = 1920;
          let { width, height } = img;

          if (width <= MAX_DIM && height <= MAX_DIM && file.size < 1024 * 1024) {
            resolve(file);
            return;
          }

          if (width > MAX_DIM || height > MAX_DIM) {
            const ratio = Math.min(MAX_DIM / width, MAX_DIM / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (!blob) { resolve(file); return; }
              const compressed = new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' });
              const savedKb = Math.round((file.size - compressed.size) / 1024);
              if (savedKb > 50) {
                console.log(`[Compress] ${file.name}: ${Math.round(file.size/1024)}KB -> ${Math.round(compressed.size/1024)}KB (-${savedKb}KB)`);
              }
              resolve(compressed.size < file.size ? compressed : file);
            },
            'image/jpeg',
            0.82
          );
        };
        img.onerror = () => resolve(file);
        img.src = e.target.result;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });
  };

  /**
   * Upload file using chunked upload for reliability
   */
  const uploadFile = async (file, retryCount = 0) => {
    const validation = validateFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return null;
    }
    
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Sesja wygasła. Zaloguj się ponownie.');
      setUploadStatus('error');
      setLastError('session_expired');
      return null;
    }
    
    // Check network
    if (!navigator.onLine) {
      toast.error('Brak połączenia z internetem.');
      setUploadStatus('error');
      setLastError('offline');
      return null;
    }
    
    setUploadStatus('uploading');
    setStatusMessage('Przygotowanie...');
    setUploadProgress(0);
    setLastFile(file);
    setLastError(null);
    
    // Create abort controller for this upload
    abortControllerRef.current = new AbortController();
    
    try {
      const result = await chunkedUpload(
        file, 
        token, 
        (progress, message) => {
          setUploadProgress(progress);
          setStatusMessage(message);
          if (progress >= 90) {
            setUploadStatus('processing');
          }
        },
        abortControllerRef.current.signal
      );
      
      setUploadStatus('success');
      setStatusMessage('Gotowe!');
      setUploadProgress(100);
      return result;
      
    } catch (e) {
      console.error('Upload error:', e);
      
      // Handle abort
      if (e.name === 'AbortError') {
        setUploadStatus('');
        setStatusMessage('');
        return null;
      }
      
      setUploadStatus('error');
      
      // Determine error type and message
      let errorMsg = e.message || 'Błąd uploadu';
      let errorType = 'unknown';
      
      if (e.message?.includes('timeout') || e.message?.includes('części')) {
        errorType = 'timeout';
      } else if (e.message?.includes('Sesja') || e.message?.includes('401') || e.message?.includes('403')) {
        errorMsg = 'Sesja wygasła. Zaloguj się ponownie.';
        errorType = 'auth';
      } else if (e.message?.includes('duży')) {
        errorType = 'too_large';
      } else if (!navigator.onLine) {
        errorMsg = 'Brak połączenia z internetem.';
        errorType = 'offline';
      }
      
      setLastError(errorType);
      
      // Auto-retry for timeout errors (max 2 retries)
      if ((errorType === 'timeout' || errorType === 'unknown') && retryCount < 2) {
        toast.info(`Ponawiam upload... (próba ${retryCount + 2}/3)`);
        await new Promise(r => setTimeout(r, 2000));
        return uploadFile(file, retryCount + 1);
      }
      
      toast.error(errorMsg);
      return null;
    }
  };

  const handleRetry = async () => {
    if (lastFile) {
      setUploadingWithNotify(true);
      const result = await uploadFile(lastFile);
      if (result) {
        onCoverChange(result.urls.large);
        toast.success('Zdjęcie główne dodane!');
      }
      setUploadingWithNotify(false);
    }
  };

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setUploadingWithNotify(false);
    setUploadStatus('');
    setStatusMessage('');
    setUploadProgress(0);
  };

  // Open cropper for file
  const openCropper = (file, type) => {
    const reader = new FileReader();
    reader.onload = () => {
      setCropperImage(reader.result);
      setCropperType(type);
      setPendingFile(file);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  // Handle cropped image
  const handleCropComplete = async (croppedBlob) => {
    setShowCropper(false);
    const file = new File([croppedBlob], pendingFile?.name || 'cropped.jpg', { type: 'image/jpeg' });
    
    if (cropperType === 'cover') {
      await handleCoverUploadDirect(file);
    } else {
      await handleGalleryUploadDirect([file]);
    }
    
    setCropperImage(null);
    setPendingFile(null);
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Open cropper for cover images
    openCropper(file, 'cover');
    e.target.value = '';
  };

  const handleCoverUploadDirect = async (file) => {
    setUploadingWithNotify(true);
    const compressed = await compressImage(file);
    const result = await uploadFile(compressed);
    if (result) {
      onCoverChange(result.urls.large);
      toast.success('Zdjecie glowne dodane!');
    }
    setUploadingWithNotify(false);
  };

  const handleGalleryUploadDirect = async (files) => {
    const remaining = maxGalleryImages - gallery.length;
    if (remaining <= 0) {
      toast.error(`Maksymalnie ${maxGalleryImages} zdjęć w galerii`);
      return;
    }
    
    const filesToUpload = files.slice(0, remaining);
    setUploadingWithNotify(true);
    
    const newImages = [];
    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];
      setStatusMessage(`Kompresja i upload ${i + 1}/${filesToUpload.length}...`);
      const compressed = await compressImage(file);
      const result = await uploadFile(compressed);
      if (result) {
        newImages.push(result.urls?.medium || result.url);
      }
    }
    
    if (newImages.length > 0) {
      // Pass the new combined array directly
      const newGallery = [...gallery, ...newImages];
      console.log('[ImageUploader] Gallery updated:', { 
        oldCount: gallery.length, 
        newCount: newGallery.length,
        newImages 
      });
      onGalleryChange(newGallery);

      // Auto-set cover from first uploaded gallery photo if cover not yet set
      // (UX fix: breeders often upload gallery first and forget separate cover)
      if (!coverImage) {
        onCoverChange(newImages[0]);
        toast.success(`Dodano ${newImages.length} zdjęć — pierwsze ustawiono jako główne`);
      } else {
        toast.success(`Dodano ${newImages.length} zdjęć`);
      }
    }
    setUploadingWithNotify(false);
  };

  const setAsCover = (img) => {
    onCoverChange(img);
    toast.success('Ustawiono jako zdjęcie główne');
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    const remaining = maxGalleryImages - gallery.length;
    if (remaining <= 0) {
      toast.error(`Maksymalnie ${maxGalleryImages} zdjęć w galerii`);
      return;
    }
    
    // For single file, open cropper. For multiple, upload directly
    if (files.length === 1) {
      openCropper(files[0], 'gallery');
    } else {
      await handleGalleryUploadDirect(files);
    }
    e.target.value = '';
  };

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files).filter(f => 
      f.type.startsWith('image/')
    );
    
    if (files.length === 0) {
      toast.error('Upuść pliki graficzne (JPG, PNG, WEBP)');
      return;
    }
    
    const remaining = maxGalleryImages - gallery.length;
    if (remaining <= 0) {
      toast.error(`Maksymalnie ${maxGalleryImages} zdjęć w galerii`);
      return;
    }
    
    await handleGalleryUploadDirect(files.slice(0, remaining));
  }, [gallery, maxGalleryImages]);

  const removeCover = () => {
    onCoverChange('');
    setLastFile(null);
    setLastError(null);
  };

  const removeGalleryImage = (index) => {
    const newGallery = gallery.filter((_, i) => i !== index);
    onGalleryChange(newGallery);
  };

  const moveGalleryImage = (fromIndex, toIndex) => {
    const newGallery = [...gallery];
    const [removed] = newGallery.splice(fromIndex, 1);
    newGallery.splice(toIndex, 0, removed);
    onGalleryChange(newGallery);
  };

  // Error icon based on type
  const getErrorIcon = () => {
    if (lastError === 'offline') return <WifiOff className="w-10 h-10 mx-auto mb-2" />;
    return <AlertCircle className="w-10 h-10 mx-auto mb-2" />;
  };

  return (
    <div className="space-y-6">
      {/* Image Cropper Modal */}
      {showCropper && cropperImage && (
        <ImageCropper
          imageSrc={cropperImage}
          onCropComplete={handleCropComplete}
          onCancel={() => {
            setShowCropper(false);
            setCropperImage(null);
            setPendingFile(null);
          }}
          aspectRatio={cropperType === 'cover' ? 16/9 : null}
        />
      )}

      {/* Global Upload Status Overlay */}
      {uploading && (
        <div className="fixed bottom-4 right-4 z-40 bg-slate-900 border border-white/20 rounded-2xl p-4 shadow-xl min-w-[300px]">
          <div className="flex items-center gap-3">
            {uploadStatus === 'success' ? (
              <div className="w-10 h-10 rounded-xl bg-[#F47A3B]/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-[#F47A3B]" />
              </div>
            ) : uploadStatus === 'error' ? (
              <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-400" />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
              </div>
            )}
            <div className="flex-1">
              <p className="text-white font-medium text-sm">{statusMessage || 'Przesyłanie...'}</p>
              <div className="mt-2">
                <Progress value={uploadProgress} className="h-2 bg-white/10" />
              </div>
              <p className="text-zinc-400 text-xs mt-1">
                {uploadProgress}% • Chunked upload (niezawodny)
              </p>
            </div>
            {uploading && uploadStatus !== 'success' && (
              <button
                onClick={handleCancel}
                className="text-zinc-400 hover:text-white p-1"
                title="Anuluj"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Cover Image */}
      <div>
        <label className="text-gray-300 mb-3 block font-medium flex items-center gap-2">
          <ImageIcon className="w-4 h-4" />
          Zdjęcie główne (cover)
          <span className="text-xs text-zinc-500 ml-auto flex items-center gap-1">
            <Crop className="w-3 h-3" /> z kadrowaniem
          </span>
        </label>
        <div className="relative">
          {coverImage ? (
            <div className="relative rounded-2xl overflow-hidden aspect-video bg-gray-900">
              <img 
                src={getImageUrl(coverImage)} 
                alt="Cover" 
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeCover();
                }}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white shadow-lg z-10"
                data-testid="remove-cover-btn"
              >
                <X className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => coverInputRef.current?.click()}
                className="absolute bottom-3 right-3 px-4 py-2 rounded-full bg-black/50 hover:bg-black/70 text-white text-sm transition-colors"
              >
                Zmień
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => coverInputRef.current?.click()}
              disabled={uploading}
              className="w-full aspect-video rounded-2xl border-2 border-dashed border-white/20 hover:border-blue-500/50 bg-white/5 flex flex-col items-center justify-center gap-3 transition-colors cursor-pointer"
              data-testid="cover-upload-btn"
            >
              {uploading && cropperType === 'cover' ? (
                <div className="flex flex-col items-center gap-3 w-full max-w-xs">
                  <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
                  <div className="w-full">
                    <Progress value={uploadProgress} className="h-2 bg-white/10" />
                    <p className="text-zinc-400 text-sm mt-2 text-center">
                      {statusMessage || `${uploadProgress}%`}
                    </p>
                  </div>
                </div>
              ) : lastError ? (
                <div className="flex flex-col items-center gap-3">
                  <div className="text-red-400 text-center">
                    {getErrorIcon()}
                    <p className="text-sm">Upload nie powiódł się</p>
                    <p className="text-xs text-zinc-500 mt-1">
                      {lastError === 'offline' ? 'Sprawdź połączenie' : 'Spróbuj ponownie'}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); handleRetry(); }}
                    className="text-blue-400 border-blue-400/50 hover:bg-blue-400/10"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Spróbuj ponownie
                  </Button>
                </div>
              ) : (
                <>
                  <ImageIcon className="w-10 h-10 text-zinc-500" />
                  <span className="text-zinc-400">Kliknij aby dodać zdjęcie główne</span>
                  <span className="text-zinc-500 text-sm">JPG, PNG lub WEBP do 12MB</span>
                  <span className="text-[#F47A3B]/70 text-xs">✓ Chunked upload - działa na LTE</span>
                </>
              )}
            </button>
          )}
          <input
            ref={coverInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleCoverUpload}
            className="hidden"
            data-testid="cover-upload-input"
          />
        </div>
      </div>

      {/* Gallery */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-gray-300 font-medium">
            Galeria ({gallery.length}/{maxGalleryImages})
          </label>
          {gallery.length > 0 && (
            <span className="text-zinc-500 text-sm">Przeciągnij aby zmienić kolejność</span>
          )}
        </div>
        
        {/* Gallery Grid */}
        <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mb-4">
          {gallery.map((img, index) => {
            const isCover = coverImage && (img === coverImage || getImageUrl(img) === getImageUrl(coverImage));
            return (
            <div 
              key={`${img}-${index}`}
              className={`relative aspect-square rounded-xl overflow-hidden bg-gray-900 group ${isCover ? 'ring-2 ring-[#F47A3B]' : ''}`}
              draggable
              onDragStart={(e) => e.dataTransfer.setData('text/plain', index.toString())}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
                moveGalleryImage(fromIndex, index);
              }}
            >
              <img 
                src={getImageUrl(img)} 
                alt={`Gallery ${index + 1}`} 
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeGalleryImage(index);
                }}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white shadow-lg z-10"
                data-testid={`remove-gallery-${index}`}
              >
                <X className="w-4 h-4" />
              </button>
              <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-black/50 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all cursor-grab">
                <GripVertical className="w-3 h-3" />
              </div>
              {isCover ? (
                <span className="absolute bottom-2 left-2 right-2 text-white text-[11px] font-semibold bg-[#F47A3B] px-2 py-1 rounded text-center shadow-lg">
                  ★ Zdjęcie główne
                </span>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setAsCover(img);
                  }}
                  className="absolute bottom-2 left-2 right-2 text-white text-[11px] font-semibold bg-black/70 hover:bg-[#F47A3B] px-2 py-1 rounded text-center shadow-lg opacity-0 group-hover:opacity-100 transition-all"
                  data-testid={`set-cover-${index}`}
                >
                  Ustaw jako główne
                </button>
              )}
              <span className="absolute top-2 left-1/2 -translate-x-1/2 text-white text-xs bg-black/50 px-2 py-0.5 rounded">
                {index + 1}
              </span>
            </div>
          );})}
          
          {/* Add more button */}
          {gallery.length < maxGalleryImages && (
            <button
              type="button"
              onClick={() => galleryInputRef.current?.click()}
              disabled={uploading}
              className="aspect-square rounded-xl border-2 border-dashed border-white/20 hover:border-blue-500/50 bg-white/5 flex flex-col items-center justify-center gap-2 transition-colors cursor-pointer"
              data-testid="add-gallery-btn"
            >
              {uploading && cropperType === 'gallery' ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
                  <span className="text-zinc-400 text-xs">{uploadProgress}%</span>
                </div>
              ) : (
                <>
                  <Upload className="w-6 h-6 text-zinc-500" />
                  <span className="text-zinc-500 text-xs">Dodaj</span>
                </>
              )}
            </button>
          )}
        </div>

        {/* Drop zone for gallery */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
            dragOver 
              ? 'border-blue-500 bg-blue-500/10' 
              : 'border-white/10 hover:border-white/20'
          }`}
        >
          <Upload className={`w-8 h-8 mx-auto mb-2 ${dragOver ? 'text-blue-400' : 'text-zinc-500'}`} />
          <p className={dragOver ? 'text-blue-400' : 'text-zinc-400'}>
            Przeciągnij zdjęcia tutaj lub{' '}
            <button
              type="button"
              onClick={() => galleryInputRef.current?.click()}
              className="text-blue-400 hover:underline"
            >
              wybierz z dysku
            </button>
          </p>
          <p className="text-zinc-500 text-sm mt-1">
            Możesz dodać do {maxGalleryImages - gallery.length} zdjęć
          </p>
          <p className="text-[#F47A3B]/60 text-xs mt-2">
            ✓ Chunked upload - stabilny nawet na wolnym łączu
          </p>
        </div>

        <input
          ref={galleryInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          onChange={handleGalleryUpload}
          className="hidden"
          data-testid="gallery-upload-input"
        />
      </div>

      {/* Legal notice */}
      <p className="text-zinc-500 text-xs text-center mt-4">
        📋 Publikuj tylko materiały, do których masz prawa.
      </p>
    </div>
  );
}

export default ImageUploader;
